// frontend/src/visitor_ai/hooks/useVisitorAI.js
import { useCallback } from "react";
import VisitorAIService from "../services/VisitorAIService";
import { useVisitorAIContext } from "../context/VisitorAIContext";

/**
 * useVisitorAI hook
 * - sendMessage(text, pageContext)
 * - executeAction(actionSchema, pageContext)
 *
 * Normalizes backend response into message objects and pushes to context.
 */

export default function useVisitorAI() {
  const {
    addMessage,
    setLoading,
    setPendingAction,
    setRetrievalHint,
    setMissingFields,
    setLastQuery,
  } = useVisitorAIContext();

  const normalizeAndPush = (raw, userText = null) => {
    // raw may contain: summary, result, ui_hint, intent, action_schema, status, retrieval_hint
    const aiMsg = {
      id: Date.now(),
      sender: "ai",
      text: raw.summary || raw.message || "",
      ui_hint: raw.ui_hint || null,
      result: raw.result || null,
      intent: raw.intent || null,
      action_schema: raw.action_schema || null,
      status: raw.status || null,
      retrieval_hint: raw.retrieval_hint || null,
      raw,
    };

    // push the AI message
    addMessage(aiMsg);

    // update context-level things
    if (aiMsg.action_schema) setPendingAction(aiMsg.action_schema);
    else setPendingAction(null);

    if (aiMsg.retrieval_hint) setRetrievalHint(aiMsg.retrieval_hint);
    else setRetrievalHint(null);

    if (aiMsg.status === "needs_info") {
      // backend may include missing fields in action_schema.params.missing_fields or ui_hint.missing_fields
      const missing = raw.missing_fields || (aiMsg.action_schema && aiMsg.action_schema.missing_fields) || raw.ui_hint?.missing_fields || null;
      setMissingFields(missing);
    } else {
      setMissingFields(null);
    }
  };

  const sendMessage = useCallback(
    async (text, pageContext = {}) => {
      if (!text) return;
      setLoading(true);

      // push user message first
      addMessage({ id: Date.now() + "-u", sender: "user", text });

      try {
        setLastQuery(text);
        const raw = await VisitorAIService.queryVisitorAI({
          text,
          page_context: pageContext,
        });

        normalizeAndPush(raw, text);
        setLoading(false);
        return raw;
      } catch (err) {
        setLoading(false);
        addMessage({ id: Date.now() + "-err", sender: "system", text: `Error: ${err.message}` });
        throw err;
      }
    },
    [addMessage, setLoading, setLastQuery]
  );

  const executeAction = useCallback(
    async (actionSchema, pageContext = {}, confirm = true) => {
      if (!actionSchema) throw new Error("No action schema provided");
      setLoading(true);
      try {
        const raw = await VisitorAIService.executeAction({
          action_schema: actionSchema,
          page_context: pageContext,
          confirm,
        });
        // after execution, push a system/ai message with result
        normalizeAndPush(raw, null);
        setLoading(false);
        return raw;
      } catch (err) {
        setLoading(false);
        addMessage({ id: Date.now() + "-err", sender: "system", text: `Action error: ${err.message}` });
        throw err;
      }
    },
    [setLoading]
  );

  return { sendMessage, executeAction };
}
